import { CircleCheckIcon, CircleCrossIcon } from "@xola/icons";
import { Button } from "@xola/ui-kit";
import { hasGiftRecipient, isMembershipItem, isOtherSellerPurchase, PurchaseItemStatus } from "common";
import { head, xorBy } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    Experience,
    ExperiencePurchaseItem,
    GiftPurchaseItem,
    Membership,
    Permission,
    Purchase,
    PurchaseItem,
    Tag,
    TagId,
} from "types";
import { IsGranted } from "../../Common/IsGranted";
import { TagInput } from "../../Common/TagInput";
import { AcceptOrDeclinePurchaseModal } from "../../Dashboard/AcceptOrDeclinePurchaseModal/AcceptOrDeclinePurchaseModal";
import { CancellationProtectionBadge } from "../CancellationProtectionBadge";
import { SplitPayAttendeesAndPayers } from "../SplitPayment/SplitPayAttendeesAndPayers";
import { CollectCustomerReviewToggle } from "./CollectCustomerReviewToggle";
import { DisputeActions } from "./DisputeActions";
import { PurchaseContactInformation } from "./PurchaseContactInformation";
import { PurchaseInfoBadge } from "./PurchaseInfo";
import { api } from "@/api/api";
import { useInvalidate } from "@/hooks/useInvalidation";
import { hasUniqueGuestIdentification } from "@/models/Experience";
import { isDisputeSubmitted, isPending } from "@/models/Purchase";
import { isExperiencePurchaseItem } from "@/models/PurchaseItem";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

interface PurchaseCustomerInformationProps {
    purchase: Purchase;
    item?: PurchaseItem | GiftPurchaseItem;
    product?: Experience | Membership;
    onContactInfoClick?: () => void;
}

export const PurchaseCustomerInformation = ({
    purchase,
    item: selectedItem,
    product,
    onContactInfoClick,
}: PurchaseCustomerInformationProps) => {
    const item = selectedItem ?? purchase.items[0];
    const isMembership = isMembershipItem(item);
    const isExperience = isExperiencePurchaseItem(item);
    const isAttendeeUnique = hasUniqueGuestIdentification(product);
    const isMistItem = !!selectedItem && purchase.items.length > 1;
    const showDispute = isDisputeSubmitted(purchase) && !isMistItem;

    return (
        <div className="w-full space-y-10">
            {selectedItem && isPending(selectedItem) && (
                <IsGranted permissions={Permission.MODIFY_BOOKING_LITE}>
                    <AcceptDeclineContainer purchase={purchase} purchaseItem={selectedItem} />
                </IsGranted>
            )}

            <div className="space-y-4">
                {isExperience && (
                    <div className="flex items-center justify-between">
                        <div className="text-xl font-bold">Reservation Confirmation</div>
                    </div>
                )}
                <div className="flex items-center justify-between">
                    {/* Show short code info and purchase/item status */}
                    <div className="flex items-center space-x-2">
                        <PurchaseInfoBadge purchase={purchase} item={selectedItem} />
                        <CancellationProtectionBadge item={selectedItem} />
                    </div>
                    {showDispute && <DisputeActions purchase={purchase} />}
                </div>
            </div>

            <PurchaseContactInformation
                title="Contact Information"
                contactId={purchase.organizer?.id}
                purchase={purchase}
                item={item}
                onContactInfoClick={onContactInfoClick}
            />

            <CollectCustomerReviewToggle purchase={purchase} item={item} />

            {hasGiftRecipient(item) && (
                <PurchaseContactInformation
                    title="Recipient Information"
                    contactId={item.recipient?.id}
                    recipientName={item.recipientName}
                    recipientEmail={item.recipientEmail}
                />
            )}

            {(isExperience || isMembership) && (
                <SplitPayAttendeesAndPayers
                    isAttendeeUnique={isAttendeeUnique}
                    purchase={purchase}
                    item={selectedItem as ExperiencePurchaseItem}
                />
            )}
            <IsGranted permissions={Permission.PURCHASE_TAGS_VIEW}>
                <Tags purchase={purchase} />
            </IsGranted>
        </div>
    );
};

const Tags = ({ purchase }: { purchase: Purchase }) => {
    const invalidate = useInvalidate();
    const seller = getSeller();

    const addTag = useCallback(
        async (tag: Tag) => {
            return api.post(`/purchases/${purchase.id}/tags`, tag);
        },
        [purchase],
    );

    const deleteTag = useCallback(
        async (tag: Tag) => {
            return api.delete(`/purchases/${purchase.id}/tags/${encodeURIComponent(tag.id)}`);
        },
        [purchase],
    );

    const { mutateAsync: handleTagsChange } = useMutation(
        async (value: Tag[]) => {
            const purchaseTags = purchase.tags ?? [];
            const tag = head(xorBy(purchaseTags, value, "id")); // tag to add or remove

            if (tag) {
                // Perform the actual update
                await (purchaseTags.some(({ id }) => id === tag.id) ? deleteTag(tag) : addTag(tag));
                // Optimistic update otherwise invalidate and re-display takes too long
                updateCache("purchases", { ...purchase, tags: value });
            }
        },
        {
            onSuccess: () => {
                invalidate("purchases", purchase.id);
            },
        },
    );

    const sanitizedTags = useMemo(() => {
        if (!purchase.tags) return [];

        return purchase.tags.map((tag) => {
            if (tag.id.startsWith(TagId.ID_VERIFIED) || tag.id.startsWith(TagId.ID_NOT_VERIFIED)) {
                return { ...tag, id: tag.id.replace(/ #[a-f\d]{24}$/, "") };
            }

            return tag;
        });
    }, [purchase.tags]);

    return (
        <div className="space-y-4">
            <div className="text-xl font-bold">Tags</div>
            <TagInput
                value={sanitizedTags}
                isDisabled={isOtherSellerPurchase(purchase, seller.id)}
                onChange={handleTagsChange}
            />
        </div>
    );
};

interface AcceptDeclineContainerProps {
    purchase: Purchase;
    purchaseItem: PurchaseItem;
}

const AcceptDeclineContainer = ({ purchase, purchaseItem }: AcceptDeclineContainerProps) => {
    const [activePurchase, setActivePurchase] = useState<Purchase>();
    const [updatedStatus, setUpdatedStatus] = useState<
        PurchaseItemStatus.CONFIRMED | PurchaseItemStatus.DECLINED | null
    >(null);

    const handleAcceptOrDeclineModalClose = () => {
        setActivePurchase(undefined);
        setUpdatedStatus(null);
    };

    const handleAcceptOrDeclineModalOpen = (status: PurchaseItemStatus.CONFIRMED | PurchaseItemStatus.DECLINED) => {
        setActivePurchase(purchase);
        setUpdatedStatus(status);
    };

    return (
        <div className="space-y-4 rounded border border-gray-light p-4">
            <div className="flex items-center">
                <div className="text-xl font-bold">Pending Confirmation</div>
            </div>

            <div className="flex items-center space-x-4">
                <Button
                    color="danger"
                    icon={<CircleCrossIcon size="medium" />}
                    onClick={() => handleAcceptOrDeclineModalOpen(PurchaseItemStatus.DECLINED)}
                >
                    Decline
                </Button>
                <Button
                    color="success"
                    icon={<CircleCheckIcon size="medium" />}
                    onClick={() => handleAcceptOrDeclineModalOpen(PurchaseItemStatus.CONFIRMED)}
                >
                    Confirm
                </Button>
                {activePurchase && updatedStatus !== null && (
                    <AcceptOrDeclinePurchaseModal
                        isOpen={!!updatedStatus}
                        purchase={activePurchase}
                        item={purchaseItem}
                        updatedStatus={updatedStatus}
                        onClose={handleAcceptOrDeclineModalClose}
                    />
                )}
            </div>
        </div>
    );
};
